import {
  Breadcrumb,
  Drawer,
  Flex,
  Grid,
  Image,
  Layout,
  Menu,
  MenuProps,
  Typography,
  theme,
} from "antd";
import Sider from "antd/es/layout/Sider";
import { Content, Footer } from "antd/es/layout/layout";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { matchPath, useLocation, useNavigate } from "react-router-dom";
import { AppRoutes } from "../../Routes";
import Logo_Default from "../../assets/images/logo_default_transparent.svg";
import Logo_Icon from "../../assets/images/logo_icon_default.svg";
import Logo_White from "../../assets/images/logo_white_transparent.svg";
import { useThemeContext } from "../../context/theme/context";
import { RobinHeader } from "./Header";
import { useAppContext } from "../../context/app/context";

const { useBreakpoint } = Grid;

const { Link } = Typography;

type MenuItem = Required<MenuProps>["items"][number];

export function RobinLayout(props: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const themeContext = useThemeContext();
  const { t } = useTranslation();
  const { lg } = useBreakpoint();
  const appContext = useAppContext();
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  // States
  const [selectedKey, setSelectedKey] = useState<string>(
    window.location.pathname,
  );
  const [isSiderCollapsed, setIsSiderCollapsed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Effects
  useEffect(() => {
    setSelectedKey(window.location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.pathname]);

  // Get user permissions
  const user = appContext.state.user;
  const permissionNames = user?.permissionNames || [];
  const isStoreAdmin =
    permissionNames.includes("store admin") &&
    !permissionNames.includes("Super Partner");
  const isSuperPartner = permissionNames.includes("Super Partner");

  // Filter routes based on permissions
  const filteredRoutes = AppRoutes.filter((rec) => {
    if (!rec.showInMenu || rec.asChild) return false;

    // Store admin can only see operation panel
    if (isStoreAdmin) {
      return rec.path.startsWith("/operation-panel");
    }

    // Super Partner can see everything
    if (isSuperPartner) {
      return true;
    }

    // For other users, show all routes (they should have been filtered by useAuthenticate)
    return true;
  });

  const menuItems: MenuItem[] = filteredRoutes.map((route) => ({
    label: route.label ? t(route.label) : "",
    key: route.path,
    icon: route.icon,
    onClick: () => !route.asParent && navigate(route.path),
    children: route.asParent
      ? AppRoutes.filter((childRoute) => {
          if (!childRoute.asChild || !childRoute.path.startsWith(route.path)) {
            return false;
          }

          // Store admin can only see operation panel child routes
          if (isStoreAdmin) {
            return childRoute.path.startsWith("/operation-panel");
          }

          // Super Partner can see everything
          if (isSuperPartner) {
            return true;
          }

          // For other users, show all child routes
          return true;
        }).map((childRoute) => ({
          label: childRoute.label ? t(childRoute.label) : "",
          key: childRoute.path,
          icon: childRoute.icon,
          onClick: () => navigate(childRoute.path),
        }))
      : undefined,
  }));

  const pathSegments = location.pathname.split("/").filter(Boolean);
  const accumulatedPaths = pathSegments.map(
    (_, index) => `/${pathSegments.slice(0, index + 1).join("/")}`,
  );

  const getBreadcrumbTitle = (path: string) => {
    const matchedRoute = AppRoutes.find((route) =>
      matchPath({ path: route.path, end: true }, path),
    );
    if (matchedRoute?.label) return t(matchedRoute.label);

    // Fallbacks for routes without labels (e.g., mutate pages)
    if (matchPath({ path: "/menu/products/mutate/:id", end: true }, path)) {
      return t("title.edit_product");
    }
    if (matchPath({ path: "/menu/products/mutate", end: true }, path)) {
      return t("title.add_product");
    }

    // Last resort: show raw segment
    return pathSegments[accumulatedPaths.indexOf(path)] ?? path;
  };

  const breadcrumbItems = (accumulatedPaths.length
    ? accumulatedPaths
    : ["/dashboard"]
  ).map((path, index) => ({
    title: (
      <Link
        key={path}
        onClick={() => {
          navigate(path);
        }}
      >
        {getBreadcrumbTitle(path)}
      </Link>
    ),
  }));

  const menuContent = (
    <>
      <Flex align="center" justify="center" style={{ padding: "20px" }}>
        <Image
          preview={false}
          src={
            isSiderCollapsed && lg
              ? Logo_Icon
              : themeContext.state.mode === "dark"
                ? Logo_White
                : Logo_Default
          }
        />
      </Flex>
      <Menu
        selectedKeys={[selectedKey]}
        defaultSelectedKeys={["1"]}
        theme={themeContext.state.mode}
        mode="inline"
        items={menuItems}
        onClick={() => !lg && setDrawerOpen(false)}
      />
    </>
  );

  return (
    <Layout
      style={{ minHeight: "100vh", maxWidth: "100vw", overflowX: "hidden" }}
    >
      {/* Sider for desktop (lg and above) */}
      {lg && (
        <Sider
          collapsible
          collapsed={isSiderCollapsed}
          onCollapse={(value: any) => setIsSiderCollapsed(value)}
          theme={themeContext.state.mode}
          width={256}
        >
          {menuContent}
        </Sider>
      )}

      {/* Drawer for mobile/tablet (below lg) */}
      {!lg && (
        <Drawer
          title={null}
          placement="left"
          onClose={() => setDrawerOpen(false)}
          open={drawerOpen}
          styles={{ body: { padding: 0 } }}
          width={256}
        >
          {menuContent}
        </Drawer>
      )}

      <Layout style={{ maxWidth: "100%", overflowX: "hidden" }}>
        <RobinHeader
          onMenuClick={!lg ? () => setDrawerOpen(true) : undefined}
        />
        <Content
          style={{ padding: "0 16px", maxWidth: "100%", overflowX: "hidden" }}
        >
          <Breadcrumb style={{ padding: "16px 0" }} items={breadcrumbItems} />
          <div
            style={{
              padding: 24,
              minHeight: "100%",
              background: colorBgContainer,
              borderRadius: borderRadiusLG,
            }}
          >
            {props.children}
          </div>
        </Content>
        <Footer style={{ textAlign: "center" }}>
          RobinFood ©{new Date().getFullYear()}
        </Footer>
      </Layout>
    </Layout>
  );
}
